import { useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../../../helper/Breadcrumb";
import axiosInstance from "../../../axiosInterceptor";
import { FiUploadCloud } from "react-icons/fi";
import ZipUpload from "./ZipUpload";

const UploadSheet = () => {
 const breadcrumbdata = [
  { label: "Home", path: "/" },
  { label: "Upload Sheets", path: "*" },
 ];

 const [fileName, setFileName] = useState("");
 const [progress, setProgress] = useState(0);
 const [isUploading, setIsUploading] = useState(false);
 const [apiErrors, setApiErrors] = useState([]);
 const [successMessage, setSuccessMessage] = useState("");

 const inputRef = useRef(null);

 const validationSchema = Yup.object({
  file: Yup.mixed()
   .required("Please upload an Excel file")
   .test(
    "fileFormat",
    "Only .xlsx, .xls, or .csv files are allowed",
    (value) => {
     if (!value) return false;
     const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
     ];
     return allowedTypes.includes(value.type);
    }
   ),
 });

 const formik = useFormik({
  initialValues: { file: null },
  validationSchema,
  onSubmit: (values) => handleFileUpload(values.file),
 });

 const handleDragOver = (e) => e.preventDefault();
 const handleDrop = (e) => {
  e.preventDefault();
  const file = e.dataTransfer.files[0];
  if (file) {
   formik.setFieldValue("file", file);
   setFileName(file.name);
   setApiErrors([]);
  }
 };
 const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
   formik.setFieldValue("file", file);
   setFileName(file.name);
   setApiErrors([]);
  }
 };

 const handleFileUpload = async (file) => {
  setIsUploading(true);
  setProgress(0);
  setApiErrors([]);
  setSuccessMessage("");

  const formData = new FormData();
  formData.append("csv", file);

  const timer = setInterval(() => {
   setProgress((prev) => (prev < 95 ? prev + 5 : prev));
  }, 300);

  try {
   const response = await axiosInstance.post("/importsheet", formData, {
    headers: { "Content-Type": "multipart/form-data" },
   });
   if (response.data?.isSuccess) {
    setProgress(100);
    setSuccessMessage(response.data.message || "File uploaded successfully!");
   } else {
    setApiErrors(["Something went wrong. Please check the file again."]);
   }
  } catch (error) {
    console.log(error);
   if (error.response && Array.isArray(error.response.data.errors)) {
    setApiErrors(error.response.data.errors);
   } else if (error.response && error.response.data.message) {
    setApiErrors([error.response.data.message]);
   } else {
    setApiErrors(["Upload failed! Please try again."]);
   }
  } finally {
   clearInterval(timer);
   setIsUploading(false);
  }
 };

 return (
  <div className="content-wrapper" style={{ padding: "40px 20px" }}>
   <div className="page-header mt-5 d-flex justify-content-between align-items-center mb-4">
    <h3 className="custom-page-title" style={{ fontWeight: 600 }}>
     Upload Sheets
    </h3>
    <Breadcrumb items={breadcrumbdata} />
   </div>

   <div
    className="d-flex justify-content-end align-items-center mb-2"
    style={{ background: "#fff", padding: "10px" }}
   >
    <a href="/Product_Sheets.csv" download>
     <button className="btn btn-outline-danger">
      <i className="fa-solid fa-file-excel fa-lg mr-2"></i>
      Download Sample
     </button>
    </a>
   </div>

   <div className="row justify-content-center">
    <div className="col-md-6">
     <div className="card shadow-lg rounded-3 p-4">
      <form onSubmit={formik.handleSubmit}>
       <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => inputRef.current.click()}
        className="drag-area d-flex flex-column align-items-center justify-content-center"
        style={{
         border: "2px dashed #6c757d",
         borderRadius: "16px",
         padding: "60px",
         textAlign: "center",
         cursor: "pointer",
         background: "#f9fafb",
         transition: "0.3s",
        }}
        onMouseEnter={(e) => {
         e.currentTarget.style.background = "#edf2f7";
         e.currentTarget.style.borderColor = "#4f46e5";
        }}
        onMouseLeave={(e) => {
         e.currentTarget.style.background = "#f9fafb";
         e.currentTarget.style.borderColor = "#6c757d";
        }}
       >
        <FiUploadCloud size={50} color="#4f46e5" className="mb-3" />
        <input
         type="file"
         accept=".xlsx, .xls, .csv"
         ref={inputRef}
         style={{ display: "none" }}
         onChange={handleFileChange}
        />
        {!fileName ? (
         <>
          <p
           style={{ fontSize: "18px", fontWeight: "500", marginBottom: "5px" }}
          >
           Drag & Drop your Excel file here
          </p>
          <p style={{ fontSize: "14px", color: "#6b7280" }}>
           or click to browse
          </p>
         </>
        ) : (
         <p style={{ fontSize: "16px", color: "#111827" }}>
          Selected File: <strong>{fileName}</strong>
         </p>
        )}
       </div>
       {formik.errors.file && formik.touched.file && (
        <p style={{ color: "red", marginTop: "10px", fontSize: "14px" }}>
         {formik.errors.file}
        </p>
       )}

       {apiErrors.length > 0 && (
        <div
         style={{
          marginTop: "20px",
          maxHeight: "300px",
          overflowY: "auto",
          border: "1px solid #fca5a5",
          borderRadius: "10px",
          background: "#fff5f5",
          padding: "15px",
          whiteSpace: "pre-wrap",
         }}
        >
         <h6
          style={{ color: "#b91c1c", marginBottom: "10px", fontWeight: 600 }}
         >
          ⚠️ {apiErrors.length} error{apiErrors.length > 1 ? "s" : ""} found:
         </h6>

         <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {apiErrors.flat(Infinity).map((err, index) => {
           const errorText =
            typeof err === "string"
             ? err
             : typeof err === "object" && err.message
               ? err.message
               : JSON.stringify(err);

           const match = errorText.match(/Row\s*(\d+):(.*)/i);
           const rowNum = match ? match[1] : null;
           const message = match ? match[2].trim() : errorText;

           return (
            <li
             key={index}
             style={{
              marginBottom: "6px",
              padding: "8px 10px",
              background: "#fee2e2",
              borderRadius: "8px",
              color: "#7f1d1d",
              fontSize: "14px",
              lineHeight: "1.4",
              whiteSpace: "pre-wrap",
             }}
            >
             {rowNum ? (
              <>
               <strong>Row {rowNum}:</strong> {message}
              </>
             ) : (
              errorText
             )}
            </li>
           );
          })}
         </ul>
        </div>
       )}

       {successMessage && (
        <div
         style={{
          marginTop: "20px",
          border: "1px solid #34d399",
          borderRadius: "10px",
          background: "#ecfdf5",
          padding: "15px",
         }}
        >
         <h6 style={{ color: "#065f46", fontWeight: 600, margin: 0 }}>
          ✅ {successMessage}
         </h6>
        </div>
       )}

       {isUploading && (
        <div
         className="progress mt-4"
         style={{ height: "28px", borderRadius: "14px", overflow: "hidden" }}
        >
         <div
          className="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          style={{
           width: `${progress}%`,
           background: "linear-gradient(90deg, #4f46e5, #3b82f6)",
           fontWeight: "500",
           color: "#fff",
          }}
         >
          {progress}%
         </div>
        </div>
       )}

      <button
  type="submit"
  className="btn w-100 mt-4 custom-outline-btn"
  disabled={isUploading || !formik.values.file}
>
  {isUploading ? "Uploading..." : "Upload"}
</button>
      </form>
     </div>
    </div>

    <div className="col-sm-6">
     <ZipUpload />
    </div>
   </div>
  </div>
 );
};

export default UploadSheet;
